import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && user !== false && user !== null) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#F4F4F0]">
      {/* Left: dark cartographic panel */}
      <div className="relative hidden md:flex bg-[#0A0A0A] text-white p-12 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(to right, #2A2A2A 1px, transparent 1px), linear-gradient(to bottom, #2A2A2A 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between h-full w-full">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 border-2 border-[#FF3E00]" />
              <p className="font-mono uppercase tracking-[0.25em] text-xs text-[#888]">
                GEO · OPS · CONSOLE
              </p>
            </div>
            <h2 className="mt-16 text-6xl font-bold tracking-tighter leading-[0.95]">
              Locate. <br /> Measure. <br /> Activate.
            </h2>
            <p className="mt-8 max-w-md text-sm text-[#bbb] leading-relaxed">
              Internal audience discovery and campaign sync. Query device density by
              coordinate, push activations to your DSP, and reconcile delivery — all
              in one console.
            </p>
          </div>
          <div className="font-mono text-[11px] text-[#666] uppercase tracking-[0.2em] space-y-1">
            <p>LAT  40.7128° N</p>
            <p>LON  74.0060° W</p>
            <p>RAD  0 · 250 · 500 · 1000 m</p>
            <p>WIN  7 · 30 · 60 · 90 d</p>
          </div>
        </div>
      </div>

      {/* Right: login form */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md"
          data-testid="login-form"
        >
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF3E00] mb-4">
            Authenticate / 01
          </p>
          <h1 className="text-5xl font-bold tracking-tighter leading-none">
            GEO_SYNC
          </h1>
          <p className="text-sm text-[#555] mt-4">
            Sign in with your internal credentials to continue.
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-[#555] mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full bg-white border border-[#E0E0D8] rounded-none px-4 py-3 text-[#0A0A0A] placeholder-[#888] focus:border-[#FF3E00] focus:outline-none focus:ring-1 focus:ring-[#FF3E00] font-mono text-sm"
                placeholder="you@geosync.io"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-[#555] mb-2">
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full bg-white border border-[#E0E0D8] rounded-none px-4 py-3 text-[#0A0A0A] placeholder-[#888] focus:border-[#FF3E00] focus:outline-none focus:ring-1 focus:ring-[#FF3E00] font-mono text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p
                data-testid="login-error"
                className="text-xs font-mono uppercase tracking-wider text-[#D91919] border border-[#D91919]/30 bg-[#D91919]/5 px-3 py-2"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full bg-[#FF3E00] text-white rounded-none px-6 py-3.5 font-semibold hover:bg-[#D93500] focus:outline-none focus:ring-2 focus:ring-[#FF3E00] focus:ring-offset-2 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
              {submitting ? "Verifying" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
